import { isNilOrEmpty } from '../../../language/module';
import { UNLIMITED_DAYS } from '../module';
import { RedemptionType } from './redemptionType';
import * as R from 'ramda';
import { Redemption } from './redemption';
import {Entitlement} from '../models';
import {HistoricalRedemption} from './historicalRedemption';
import { getResource } from '../../../language/resources/getResource';
import { toDateOnlyString } from '../../../language/time';
import { BlackoutDateMetadata, TimeSlotDateRange } from '../../../entitlementTypesMetadata/entitlementTypeMetadata';


/**
 * The maximum number of redemptions to allow for the drop-down that lets the agent select the number of entitlements
 * to redeem at a time. The main reason this limit is in place is to prevent adding too many nodes to the DOM. If a
 * guest has unlimited redemptions available, we don't want to put UNLIMITED_DAYS nodes into the DOM.
 */
export const REDEMPTION_MAX = 11;

/**
 * The maximum number of characters allowed for local redemption id 
 */
export const MAX_REDEMPTION_REFERENCE_LENGTH = 255;

/**
 * The maximum number of characters allowed for local redemption id 
 */
export const MAX_CORRECTION_NOTE_LENGTH = 255;

enum REDEMPTION_QUANTITY {
  UNLIMITED = 'Unlimited'
}

export enum REDEEMABLE_OPTIONS {
  BLACKED_OUT,
  REDEEM,
  ALL_USED
}


/**
 * Converts the server's entitlement redemption day data into something presentable to the user.
 */
export const getFriendlyDays = (
  resources: Record<string, any>,
  days: number | string | undefined,
  noDays: string = '0',
  unlimitedDays: string = getResource(resources, 'VIEW_DAYS_UNLIMITED')
): string => {
  const dayString = days ? days.toString() : '';

  if (isNilOrEmpty(dayString)) {
    return noDays;
  }

  if (dayString === UNLIMITED_DAYS.toString()) {
    return unlimitedDays;
  }

  return dayString;
};

/**
 * Finds the number of options needed for the redemption drop down
 * @param unusedDays
 * @param numOptions
 * @param usedDays
 */
export const endIndex = (unusedDays: number, numOptions: number, usedDays: number) => Math.max(
  Math.min(unusedDays, numOptions), numOptions - usedDays)


export const getValidEntitlements = (entitlements: Entitlement[]): Entitlement[] => {
  return entitlements.filter(entitlement => entitlement.isValid);
}

/**
 *  Finds the number of days to be displayed in the All Days statistic for f&f
 * @param entitlements
 */
export const getAllDays = (entitlements: Entitlement[]): string => {
  if (entitlements.length) {
    return entitlements.some(ent => ent.maxAllowed === UNLIMITED_DAYS) ?  REDEMPTION_QUANTITY.UNLIMITED
    : entitlements.map(ent => ent.maxAllowed).reduce((a, b) => a + b).toString();
  }
  return '0';
}

export const getAllAvailableDays = (entitlements: Entitlement[]): string => {
  if (entitlements.length) {
    return entitlements.some(ent => ent.maxAllowed === UNLIMITED_DAYS) ? REDEMPTION_QUANTITY.UNLIMITED
    : entitlements.map(ent => ent.available).reduce((a, b) => a + b).toString();
  }
  return '0';
}

export const getUsedDays = (entitlements: Entitlement[]): string => {
  if (entitlements.length) {
    return entitlements.map(ent => ent.used).reduce((a, b) => a + b).toString();
  }
  return '0';
}

/**
 * Returns a list of all historical redemptions for a given list of entitlements
 * allows for a flat list of redemptions to be used in the redemption history table
 * @param entitlements
 */
export const getAllRedemptionHistory = (entitlements: Entitlement[]): HistoricalRedemption[] => {
  return entitlements.map(entitlements => entitlements.redemptionHistory).flat();
}

/**
 * Returns a string with the status of an entitlement
 * If corrective redemptions are enabled for a user they will be able to redeem when all days are used
 * @param entitlement
 * @param correctiveRedemption
 */
export const checkEntitlementStatus = (entitlement: Entitlement, correctiveRedemption: boolean): REDEEMABLE_OPTIONS   => {
  if (entitlement.isValid || entitlement.invalidDescription === 'all-used') {
    if (entitlement.available > 0 || correctiveRedemption) {
      return REDEEMABLE_OPTIONS.REDEEM;
    }
    return REDEEMABLE_OPTIONS.ALL_USED;
  }
  return REDEEMABLE_OPTIONS.BLACKED_OUT;
}

/**
 * Gives the description for a redemption ID using a list of redemption type information.
 * Returns a blank string if @typeList is undefined or if @typeId can't be located in @typeList
 */
export const getRedemptionType = (
  typeList: RedemptionType[] | undefined,
  typeId: number
): string => R.path([ 'redemptionDescription' ], R.find(type => type.redemptionTypeId === typeId, typeList || [])) || '';

/**
 * Gives the external code from a given redemption or null if no external code present
 */
export const toExternalCode = (redemption: Redemption) =>
  !isNilOrEmpty(redemption.redemptionExternalCode) ? redemption.redemptionExternalCode : null;


/**
 * Convert all blackout data for an entitlement type to a list of blackout dates
 * Ignore the hours for now
 * @param metadata 
 * @returns 
 */
export const getAllBlackoutDates = (metadata: BlackoutDateMetadata[]): Date[] => {
  if (!metadata || metadata.length === 0) {
    return [];
  }

  const blackoutRangeDates = metadata.flatMap((m) => {
    return m.blackoutDateRanges.flatMap((range) => {
      return getDatesFromDateRange(range);
    })
  });

  return getDistinctDates(blackoutRangeDates);
}

const getDistinctDates = (dates: Date[]): Date[] => {
  // Use a Set to filter unique date strings, then map back to Date objects
  const uniqueDates = Array.from(
    new Set(dates.map(date => toDateOnlyString(date)))
  ).map(dateString => new Date(dateString));

  return uniqueDates;
};

/**
 * Convert a date range to a list of dates inclusive of start, end dates.
 * @param range 
 * @returns 
 */
const getDatesFromDateRange = (range: TimeSlotDateRange): Date[] => {
  const dates: Date[] = [] as Date[];
  const startDate = new Date(range.startDate);
  const endDate = new Date(range.endDate);

  while (startDate <= endDate) {
    dates.push(new Date(startDate)); 
    startDate.setDate(startDate.getDate() + 1);
  }
  return dates;
};

/**
 * Given a date and a list of blackout dates, returns true if the date is 
 * a blackout date.
 */
export const isBlackoutDate = (date: Date, blackoutDates: Date[]) => {
  return blackoutDates.some(blackoutDate => {
    return date.getTime() === blackoutDate.getTime();
  });
}
